import { PropsWithChildren, useRef, useState } from 'react';
import { css } from '@emotion/react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClientPoint,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react';
import { AnimatePresence, motion } from 'motion/react';
import { theme } from '@frontend/theme';
import { useCalendarView, useCalendarViewProps } from '../providers';
import { getTimestamp } from '../utils';
import { TimeTooltip } from './time-tooltip';

type CalendarViewContainerProps = PropsWithChildren<{
  numberOfHours: number;
  hasProviders?: boolean;
}>;

export const CalendarViewContainer = ({ children, numberOfHours }: CalendarViewContainerProps) => {
  const hoveredTimestamp = useRef('0');
  const [isOpen, setIsOpen] = useState(false);

  const { startHour, endHour } = useCalendarViewProps();
  const { backgroundView, setHoveredTimeProgressPosition, floatingTooltipData } = useCalendarView([
    'backgroundView',
    'setHoveredTimeProgressPosition',
    'floatingTooltipData',
  ]);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen && !backgroundView,
    placement: 'top',
    middleware: [offset(20), flip(), shift()],
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
  });

  const clientPoint = useClientPoint(context);
  const hover = useHover(context, { restMs: 1000 });
  const { getReferenceProps, getFloatingProps } = useInteractions([clientPoint, hover]);

  return (
    <>
      <section
        ref={refs.setReference}
        {...getReferenceProps({
          onMouseDown: () => setIsOpen(false),
          onMouseMove: (event) => {
            const timestamp = getTimestamp({
              event,
              element: refs.domReference.current,
              startHour,
              endHour,
            });

            if (!timestamp) return;

            const { formattedTime, totalMinutes } = timestamp;

            setHoveredTimeProgressPosition(totalMinutes);
            hoveredTimestamp.current = formattedTime;
          },
        })}
        css={containerStyle(numberOfHours)}
      >
        {children}
      </section>
      <AnimatePresence>
        {isOpen && !backgroundView ? (
          <motion.div
            ref={refs.setFloating}
            layout={false}
            style={floatingStyles}
            {...getFloatingProps()}
            css={{ pointerEvents: 'none', zIndex: theme.zIndex.hints }}
          >
            <TimeTooltip>
              {floatingTooltipData ? (
                <div css={{ display: 'flex', flexDirection: 'column' }}>
                  <span css={{ fontWeight: theme.font.weight.bold }}>{floatingTooltipData.name}</span>
                  <span css={{ fontSize: theme.font.size.small }}>{floatingTooltipData.value}</span>
                </div>
              ) : (
                hoveredTimestamp.current
              )}
            </TimeTooltip>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
};

const containerStyle = (noOfHours: number) =>
  css({
    display: 'grid',
    position: 'relative',
    backgroundColor: theme.colors.neutral5,
    marginTop: theme.spacing(5),
    gridTemplateRows: `repeat(${noOfHours}, 120px)`,
    gridTemplateColumns: 'auto auto 1fr',
    width: '100%',
  });
